import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import SearchableSelectWithAdd from '../components/SearchableSelectWithAdd';
import BulkActionsBar, { bulkIcons } from '../components/BulkActionsBar';
import BulkValueModal from '../components/BulkValueModal';
import { cleanMasterDescription } from '../utils/cleanMasterDescription';
import './ItemMaster.css';

const API = '/item-master';
const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

const UOM_OPTIONS = ['mm', 'cm', 'inch', 'ft', 'sqft', 'sqm', 'unit', 'rft', 'kg', 'litre', 'nos'];
/** Same presets as quotation “Material”; extras from GET /material-options */
const MATERIAL_PRESET_OPTS = [
  'Plywood and Laminate',
  'Gypsum',
  'Plywood and Glass',
  'Aluminium',
];

// ── Empty form state ──────────────────────────────────────────────────────────
const emptyForm = () => ({
  name: '',
  quoteMaterial: '',
  description: '',
  uom: '',
  rate: '',
});




// ── Add / Edit Modal ──────────────────────────────────────────────────────────
function ItemModal({ item, materialOptions, onAddMaterial, onClose, onSaved }) {
  const [form, setForm] = useState(
    item
      ? {
          name: item.name || '',
          quoteMaterial: item.quoteMaterial || item.itemCategory || item.category || '',
          description: cleanMasterDescription(item.description, item.defaultSpace) || '',
          uom: item.uom || '',
          rate: item.rates?.[0]?.clientRate ?? item.rate ?? '',
        }
      : emptyForm()
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const h = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!(form.quoteMaterial || '').trim()) {
      setError('Please choose or add a material.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const fd = new FormData();

      Object.entries(form).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) {
          fd.append(k, v);
        }
      });
      fd.append('category', form.quoteMaterial || '');
      fd.append('gst', '0');

      if (item) {
        await api.put(`${API}/${item._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post(API, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save item.');
    } finally { setSaving(false); }
  };

  const modalContent = (
    <div className="im-overlay" onClick={onClose}>
      <div className="im-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="im-modal-header">
          <h3>{item ? 'Edit Item' : 'New Item'}</h3>
          <button className="im-icon-btn" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="im-modal-body">
          {error && <div className="im-alert">{error}</div>}
          <form id="im-form" onSubmit={submit}>
            <div className="im-field" onMouseDown={(e) => e.stopPropagation()}>
              <label>Material</label>
              <SearchableSelectWithAdd
                value={form.quoteMaterial}
                onChange={(v) => setForm((f) => ({ ...f, quoteMaterial: v }))}
                options={materialOptions}
                onAddNew={onAddMaterial}
                addPrompt="to materials list"
                placeholder="Search or add material…"
                style={{ width: '100%', minWidth: 0 }}
              />
            </div>

            {/* Name */}
            <div className="im-field">
              <label>Name <span className="im-req">*</span></label>
              <input name="name" value={form.name} onChange={h} required placeholder="Item name" />
            </div>

            {/* UOM + GST row */}
            <div className="im-field">
  <label>Unit Of Measurement</label>
  <select name="uom" value={form.uom} onChange={h} required>
    <option value="">Select…</option>
    {UOM_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
  </select>
</div>

           

            {/* Description */}
            <div className="im-field">
              <label>Description</label>
              <textarea name="description" value={form.description} onChange={h} rows={3} placeholder="Item description…" />
            </div>

            <div className="im-field">
              <label>Rate</label>
              <input type="number" name="rate" value={form.rate} onChange={h} />
            </div>

            

          </form>
        </div>

        {/* Footer */}
        <div className="im-modal-footer">
          <button type="button" className="im-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" form="im-form" className="im-btn-submit" disabled={saving}>
            {saving ? 'Saving…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

// ── Main ItemMaster Page ───────────────────────────────────────────────────────
export default function ItemMaster() {
  const { user } = useAuth();
  const [items, setItems]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [colItem, setColItem]       = useState('');
  const [colMaterial, setColMaterial] = useState('');
  const [colUom, setColUom]         = useState('');
  const [page, setPage]             = useState(1);
  const [showModal, setShowModal]   = useState(false);
  const [editItem, setEditItem]     = useState(null);
  const [deleting, setDeleting]     = useState(null);
  const [showExcelMenu, setShowExcelMenu] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkValue, setBulkValue] = useState('');
  const [tableTier, setTableTier] = useState('signature');
  const [itemTierMap, setItemTierMap] = useState({});

  const LIMIT = 20;
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const canDelete = user?.role === 'admin';
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (search.trim()) params.search = search.trim();
      if (colItem.trim()) params.itemQ = colItem.trim();
      if (colMaterial.trim()) params.materialQ = colMaterial.trim();
      if (colUom.trim()) params.uomQ = colUom.trim();
      const res = await api.get(API, { params });
      if (seq !== loadSeq.current) return;
      const data = res.data?.data;
      if (!data?.items || !data?.pagination) return;
      setItems(data.items);
      setTotal(data.pagination.total);
    } catch (err) { console.error(err); }
    finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [page, search, colItem, colMaterial, colUom]);

  const [quoteMaterialOptions, setQuoteMaterialOptions] = useState(() => [...MATERIAL_PRESET_OPTS]);

  const loadQuoteMaterialOptions = useCallback(async () => {
    try {
      const r = await api.get('/material-options');
      const raw = Array.isArray(r.data) ? r.data : [];
      const fromDb = raw
        .map((x) => (typeof x === 'string' ? x : x?.name))
        .filter(Boolean);
      const extra = fromDb.filter(
        (n) => !MATERIAL_PRESET_OPTS.some((s) => s.toLowerCase() === String(n).toLowerCase())
      );
      extra.sort((a, b) => a.localeCompare(b));
      setQuoteMaterialOptions([...MATERIAL_PRESET_OPTS, ...extra]);
    } catch {
      setQuoteMaterialOptions([...MATERIAL_PRESET_OPTS]);
    }
  }, []);

  const addQuoteMaterialOption = useCallback(
    async (name) => {
      try {
        await api.post('/material-options', { name });
      } catch {
        /* duplicate / validation */
      }
      await loadQuoteMaterialOptions();
    },
    [loadQuoteMaterialOptions]
  );

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadQuoteMaterialOptions(); }, [loadQuoteMaterialOptions]);
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => items.some((item) => item._id === id)));
  }, [items]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    setDeleting(id);
    try { await api.delete(`${API}/${id}`); load(); }
    catch (err) { alert(err.response?.data?.message || 'Delete failed.'); }
    finally { setDeleting(null); }
  };

  const openEdit  = (item) => { setEditItem(item); setShowModal(true); };
  const openAdd   = () => { setEditItem(null); setShowModal(true); };
  const onSaved   = () => { load(); };
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedItems = useMemo(() => items.filter((item) => selectedSet.has(item._id)), [items, selectedSet]);
  const allVisibleSelected = items.length > 0 && items.every((item) => selectedSet.has(item._id));
  const toggleSelected = (id, checked) => {
    setSelectedIds((prev) => checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id));
  };
  const selectAllVisible = () => setSelectedIds(items.map((item) => item._id));
  const clearSelection = () => setSelectedIds([]);
  const openBulkEdit = () => {
    const first = selectedItems[0];
    if (first) openEdit(first);
  };
  const openBulkModal = (kind) => {
    const first = selectedItems[0];
    setBulkValue(kind === 'material' ? (first?.quoteMaterial || '') : (first?.uom || ''));
    setBulkModal(kind);
  };

  const handleBulkDelete = async () => {
    if (!canDelete || selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected item(s)?`)) return;
    try {
      await Promise.all(selectedIds.map((id) => api.delete(`${API}/${id}`)));
      clearSelection();
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Bulk delete failed.');
    }
  };

  const applyBulkUpdate = async () => {
    const value = String(bulkValue || '').trim();
    const payload = bulkModal === 'material' ? { quoteMaterial: value, category: value } : { uom: value };
    try {
      await Promise.all(selectedIds.map((id) => api.put(`${API}/${id}`, payload)));
      setBulkModal(null);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Bulk update failed.');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  

  const handleExport = async () => {
  const token = localStorage.getItem("ts_token")
  const res = await fetch("http://localhost:5000/api/item-master/export", {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = "item-master.xlsx";
  a.click();
};

const handleImport = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const formData = new FormData();
  formData.append("file", file);

  try {
const token = localStorage.getItem("ts_token");
const res = await fetch("http://localhost:5000/api/item-master/import", {
  method: "POST",
  headers: {
    Authorization: `Bearer ${token}`
  },
  body: formData,
});

    const data = await res.json();
    alert(data.message || "Import successful");

    load(); // refresh table
  } catch (err) {
    alert("Import failed");
  }
};

  return (
    <div className="im-root slide-up">
      {/* ── Top Actions ── */}
      <div className="im-toolbar" style={{ justifyContent: 'flex-end' }}>
        <div className="im-toolbar-right">
          {isAdmin && (
            <>
              <button className="im-btn-add" onClick={openAdd}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add Item
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
              </button>
              <div style={{ position: "relative" }}>
  <button
    className="im-btn-export"
    onClick={() => setShowExcelMenu(!showExcelMenu)}
  >
    📊 Excel ▼
  </button>

  {showExcelMenu && (
    <div style={{
      position: "absolute",
      right: 0,
      top: "45px",
      background: "#fff",
      border: "1px solid #ddd",
      borderRadius: "6px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      zIndex: 1000
    }}>
      
      <div
        style={{ padding: "10px", cursor: "pointer" }}
        onClick={() => {
          document.getElementById("excelImport").click();
          setShowExcelMenu(false);
        }}
      >
        Import Excel
      </div>

      <div
        style={{ padding: "10px", cursor: "pointer" }}
        onClick={() => {
          handleExport();
          setShowExcelMenu(false);
        }}
      >
        Export Excel
      </div>

    </div>
  )}

  <input
    type="file"
    id="excelImport"
    hidden
    onChange={handleImport}
  />
</div>
            </>
          )}
        </div>
      </div>

      {/* ── Search ── */}
      <div className="im-search-bar">
        <div className="im-search-wrap">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="im-search-ic"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input
            className="im-search-input"
            placeholder="Search items…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <span className="im-total-count">{total} items</span>
      </div>

      {/* ── Table ── */}
      <div className="im-table-wrap">
        <table className="im-table">
          <thead>
            <tr>
              {isAdmin && (
                <th style={{ width: 42 }}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                    aria-label="Select all visible items"
                  />
                </th>
              )}
              <th>Created On</th>
              <th style={{ minWidth: 200 }}>Item</th>
              <th style={{ minWidth: 140 }}>Material</th>
              <th>UOM</th>
              <th>Rate</th>
              {isAdmin && <th>Actions</th>}
            </tr>
            {/* Search row under headers */}
            <tr className="im-search-row-inner">
              {isAdmin && <td />}
              <td />
              <td>
                <input
                  value={colItem}
                  onChange={(e) => { setColItem(e.target.value); setPage(1); }}
                  placeholder="Filter name / code…"
                  aria-label="Filter by item name or code"
                />
              </td>
              <td>
                <input
                  value={colMaterial}
                  onChange={(e) => { setColMaterial(e.target.value); setPage(1); }}
                  placeholder="Filter material…"
                  aria-label="Filter by material"
                />
              </td>
              <td>
                <input
                  value={colUom}
                  onChange={(e) => { setColUom(e.target.value); setPage(1); }}
                  placeholder="Filter UOM…"
                  aria-label="Filter by unit of measure"
                />
              </td>
              <td>
                <select
                  value={tableTier}
                  onChange={(e) => {
                    setTableTier(e.target.value);
                    setItemTierMap({});
                  }}
                  style={{
                    width: '100%',
                    padding: '4px 6px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#4B5563',
                    borderRadius: 4,
                    border: '1px solid #E5E7EB',
                    background: '#fff',
                    cursor: 'pointer',
                  }}
                  aria-label="Change tier for all rows"
                >
                  <option value="signature">All: Signature</option>
                  <option value="essential">All: Essential (-10%)</option>
                  <option value="prestige">All: Prestige (+20%)</option>
                </select>
              </td>
              {isAdmin && <td />}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isAdmin ? 7 : 5} className="im-td-center"><div className="im-spinner" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={isAdmin ? 7 : 5} className="im-td-center im-empty">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                <p>No items found</p>
              </td></tr>
            ) : items.map(item => (
              <tr key={item._id}>
  {isAdmin && (
    <td onClick={(e) => e.stopPropagation()}>
      <input
        type="checkbox"
        checked={selectedSet.has(item._id)}
        onChange={(e) => toggleSelected(item._id, e.target.checked)}
        aria-label={`Select ${item.name}`}
      />
    </td>
  )}
  <td>
    {new Date(item.createdAt).toLocaleDateString()}
  </td>

  <td style={{ verticalAlign: 'top', minWidth: 200 }}>
    <div style={{ fontWeight: 600, fontSize: 13, color: '#1F2937', lineHeight: 1.35 }}>{item.name}</div>
    <div style={{ fontSize: 12, color: cleanMasterDescription(item.description, item.defaultSpace) ? '#6B7280' : '#9CA3AF', marginTop: 6, lineHeight: 1.45, wordBreak: 'break-word' }}>
      {cleanMasterDescription(item.description, item.defaultSpace) || '—'}
    </div>
  </td>

  <td style={{ verticalAlign: 'top', maxWidth: 220, wordBreak: 'break-word' }} title={item.quoteMaterial || ''}>
    {item.quoteMaterial || '—'}
  </td>

  <td>
    <span className="im-pill">{item.uom || '—'}</span>
  </td>

  <td>
    {(() => {
      const stdRate = Number(item.rates?.[0]?.clientRate ?? item.rate ?? 0);
      const essRate = Math.round(stdRate * 0.90);
      const prestRate = Math.round(stdRate * 1.20);
      const currentTier = itemTierMap[item._id] || tableTier || 'signature';

      return (
        <select
          value={currentTier}
          onChange={(e) => {
            const val = e.target.value;
            setItemTierMap((prev) => ({ ...prev, [item._id]: val }));
          }}
          onClick={(e) => e.stopPropagation()}
          style={{
            padding: '4px 8px',
            fontSize: 12,
            fontWeight: 600,
            color: '#1F2937',
            background: '#F9FAFB',
            border: '1px solid #D1D5DB',
            borderRadius: 6,
            cursor: 'pointer',
            outline: 'none',
            minWidth: 155,
          }}
          aria-label={`Select tier rate for ${item.name}`}
        >
          <option value="signature">Signature: ₹{stdRate.toLocaleString()}</option>
          <option value="essential">Essential: ₹{essRate.toLocaleString()}</option>
          <option value="prestige">Prestige: ₹{prestRate.toLocaleString()}</option>
        </select>
      );
    })()}
  </td>

  {isAdmin && (
    <td className="im-td-actions">
      <button onClick={() => openEdit(item)}>✏️</button>
      <button onClick={() => handleDelete(item._id)}>🗑️</button>
    </td>
  )}
</tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="im-pagination">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="im-page-btn">← Prev</button>
          <span className="im-page-info">Page {page} of {totalPages} · {total} items</span>
          <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="im-page-btn">Next →</button>
        </div>
      )}

      {/* ── Modal ── */}
      {showModal && (
        <ItemModal
          item={editItem}
          materialOptions={quoteMaterialOptions}
          onAddMaterial={addQuoteMaterialOption}
          onClose={() => { setShowModal(false); setEditItem(null); }}
          onSaved={onSaved}
        />
      )}

      <BulkActionsBar
        count={selectedIds.length}
        actions={[
          { key: 'edit', label: 'Edit First', icon: bulkIcons.edit, onClick: openBulkEdit },
          { key: 'material', label: 'Set Material', icon: bulkIcons.set, onClick: () => openBulkModal('material') },
          { key: 'uom', label: 'Set UOM', icon: bulkIcons.set, onClick: () => openBulkModal('uom') },
          { key: 'all', label: 'Select All', icon: bulkIcons.selectAll, onClick: selectAllVisible },
          { key: 'none', label: 'Deselect All', icon: bulkIcons.clear, onClick: clearSelection },
          canDelete && { key: 'delete', label: 'Delete', icon: bulkIcons.delete, onClick: handleBulkDelete, danger: true },
        ]}
      />

      {bulkModal && (
        <BulkValueModal
          title={bulkModal === 'material' ? 'Set material' : 'Set UOM'}
          count={selectedIds.length}
          value={bulkValue}
          onChange={setBulkValue}
          onClose={() => setBulkModal(null)}
          onApply={applyBulkUpdate}
          options={bulkModal === 'material' ? quoteMaterialOptions : UOM_OPTIONS}
          placeholder={bulkModal === 'material' ? 'Material name' : 'UOM'}
        />
      )}
    </div>
  );
}
